import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, Check, Lock, User, Eye, EyeOff } from 'lucide-react';
import Navbar from './Navbar';

const API_BASE_URL = 'https://api.4fingerstudios.com';

const GlitchText = ({ text, className }) => (
  <span className={`relative inline-block ${className} text-purple-500 animate-pulse`}>
    {text}
  </span>
);

const Input = ({ icon: Icon, className = '', ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-500/50" size={20} />
    <input
      {...props}
      className={`w-full bg-black/60 border border-purple-500/20 pl-10 p-3 text-slate-300 focus:border-purple-500 transition-colors duration-300 rounded ${className}`}
    />
  </div>
);

const PasswordInput = ({ icon = Lock, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input {...props} icon={icon} type={show ? 'text' : 'password'} />
      <button
        type="button"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-500"
        onClick={() => setShow(prev => !prev)}
      >
        {show ? <EyeOff size={20} /> : <Eye size={20} />}
      </button>
    </div>
  );
};

const LoginPage = () => {
  const [formState, setFormState] = useState('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({ text: '', type: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            navigate('/user-profile');
          }
        } catch (error) {
          console.error('Auth check failed:', error);
        }
      }
    };
    checkAuth();
  }, [navigate]);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4 } }
  };

  const cardVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { duration: 0.4, ease: "easeOut" } }
  };

  const checkPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (/[A-Z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[^A-Za-z0-9]/.test(pass)) strength++;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const pass = e.target.value;
    setPassword(pass);
    checkPasswordStrength(pass);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFeedbackMessage({ text: '', type: '' });

    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      const clientIp = ipData.ip;

      let response;
      let responseText;

      if (formState === 'signup') {
        if (!username || !email || !password) {
          throw new Error('Please fill in all required fields.');
        }

        response = await fetch(`${API_BASE_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Client-IP': clientIp
          },
          body: JSON.stringify({ username, password, email })
        });

        responseText = await response.text();

        if (!response.ok) {
          throw new Error(responseText || 'Signup failed');
        }

        setFeedbackMessage({ text: responseText || 'Account created! Please verify your email.', type: 'success' });
        setTimeout(() => navigate('/verify-email', { state: { username } }), 2000);
        setUsername('');
        setEmail('');
        setPassword('');
      } else if (formState === 'login') {
        if (!username || !password) {
          throw new Error('Please enter both username and password.');
        }

        response = await fetch(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Client-IP': clientIp
          },
          body: JSON.stringify({ username, password })
        });

        responseText = await response.text();

        if (!response.ok) {
          if (response.status === 403 && responseText.includes('Email not verified')) {
            setFeedbackMessage({ text: 'Email not verified. Redirecting to verification...', type: 'error' });
            setTimeout(() => navigate('/verify-email', { state: { username } }), 2000);
          } else {
            throw new Error(responseText || 'Login failed');
          }
          return;
        }

        const contentType = response.headers.get('Content-Type');
        let responseData = contentType?.includes('application/json') ? JSON.parse(responseText) : null;

        if (!responseData?.token) {
          throw new Error('No token received in response');
        }

        localStorage.setItem('token', responseData.token);

        const validateResponse = await fetch(`${API_BASE_URL}/api/auth/validate`, {
          headers: { 
            'Authorization': `Bearer ${responseData.token}`,
            'X-Client-IP': clientIp
          },
        });

        const validateText = await validateResponse.text();
        const validateData = validateResponse.headers.get('Content-Type')?.includes('application/json') ? JSON.parse(validateText) : null;

        if (!validateResponse.ok || !validateData) {
          throw new Error(validateText || 'Token validation failed');
        }

        navigate('/user-profile');
      }
    } catch (error) {
      setFeedbackMessage({ text: error.message, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderPasswordStrength = () => {
    if (formState !== 'signup') return null;
    return (
      <div className="mt-2 text-sm text-purple-400/70 space-y-1">
        <p className="flex items-center gap-2">
          {password.length >= 8 ? <Check size={14} /> : <AlertCircle size={14} />}
          Minimum 8 characters
        </p>
        <p className="flex items-center gap-2">
          {/[A-Z]/.test(password) ? <Check size={14} /> : <AlertCircle size={14} />}
          Uppercase letter
        </p>
        <p className="flex items-center gap-2">
          {/[0-9]/.test(password) ? <Check size={14} /> : <AlertCircle size={14} />}
          Number
        </p>
        <p className="flex items-center gap-2">
          {/[^A-Za-z0-9]/.test(password) ? <Check size={14} /> : <AlertCircle size={14} />}
          Special character
        </p>
      </div>
    );
  };

  const renderAuthForm = () => {
    const isSignup = formState === 'signup';

    return (
      <motion.section className="w-full max-w-md mx-auto px-4" variants={cardVariants}>
        <motion.div
          className="bg-black/60 border border-purple-500/20 p-6 sm:p-8 group hover:border-purple-500 transition-colors duration-300 rounded-lg"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <GlitchText text={isSignup ? "SIGN UP" : "LOGIN"} className="text-2xl sm:text-3xl font-bold mb-6" />

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-purple-400 mb-2">Username:</label>
              <Input
                icon={User}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            {isSignup && (
              <div className="space-y-2">
                <label className="block text-purple-400 mb-2">Email:</label>
                <Input
                  icon={User}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-purple-400 mb-2">Password:</label>
              <PasswordInput
                value={password}
                onChange={handlePasswordChange}
                required
              />
              {renderPasswordStrength()}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full border border-purple-500 hover:bg-purple-500/20 py-3 rounded transition-colors uppercase tracking-wider"
            >
              {isLoading ? 'LOADING...' : (isSignup ? 'SIGN UP' : 'LOGIN')}
            </button>

            {feedbackMessage.text && (
              <p className={`text-sm mt-4 flex items-center gap-2 ${feedbackMessage.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                <AlertCircle size={16} /> {feedbackMessage.text}
              </p>
            )}

            <div className="flex flex-col sm:flex-row justify-between text-sm text-slate-300 gap-2">
              <button
                type="button"
                onClick={() => {
                  setFormState(isSignup ? 'login' : 'signup');
                  setFeedbackMessage({ text: '', type: '' });
                  setUsername('');
                  setEmail('');
                  setPassword('');
                }}
                className="text-purple-500 hover:text-purple-400"
              >
                {isSignup ? 'Already have an account?' : "Don't have an account?"}
              </button>
              {!isSignup && (
                <button
                  type="button"
                  onClick={() => navigate('/reset-password')}
                  className="text-purple-500 hover:text-purple-400"
                >
                  Forgot Password?
                </button>
              )}
            </div>
          </form>
        </motion.div>
      </motion.section>
    );
  };

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-gray-900 to-slate-900 text-purple-300 font-mono flex flex-col"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <div className="fixed top-0 left-0 w-full h-full bg-gradient-to-b from-gray-900 to-slate-900 opacity-50">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.08)_0%,transparent_100%)] animate-pulse" />
      </div>

      <Navbar currentPage="Login" />

      <main className="relative flex-grow pt-20 sm:pt-40 max-w-5xl mx-auto p-4 sm:p-6 space-y-20">
        <motion.section
          className="text-center space-y-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <GlitchText text="AUTHENTICATION" className="text-4xl sm:text-6xl md:text-7xl font-black" />
          <div className="h-0.5 bg-gradient-to-r from-transparent via-purple-500 to-transparent max-w-md mx-auto" />
          <p className="text-slate-300 text-base sm:text-lg">
            Log in or sign up to manage your account!
          </p>
        </motion.section>

        {renderAuthForm()}
      </main>

      <footer className="relative bg-slate-900/80 text-slate-400 py-8 sm:py-12 border-t border-slate-800/50 w-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
            <div>
              <h4 className="text-purple-300 font-bold mb-4">4FingerStudios</h4>
            </div>
            <div>
              <h4 className="text-purple-300 font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://4fingerstudios.com" className="hover:text-purple-300 transition-colors duration-300">Home</a></li>
                <li><a href="https://4fingerstudios.com/contact" className="hover:text-purple-300 transition-colors duration-300">Contact</a></li>
                <li><a href="https://4fingerstudios.com/Privacy" className="hover:text-purple-300 transition-colors duration-300">Privacy Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-purple-300 font-bold mb-4">Contact Us</h4>
              <p className="text-sm mb-2">
                <span className="text-purple-300 mr-2">Email:</span> 4.finger.studios.com@gmail.com
              </p>
              <p className="text-sm">
                <span className="text-purple-300 mr-2">Support:</span> support@4fingerstudios.com
              </p>
            </div>
          </div>
          <div className="mt-8 sm:mt-12 pt-6 border-t border-slate-800/50 text-center text-xs">
            <p>© {new Date().getFullYear()} 4FingerStudios. All rights reserved.</p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent" />
      </footer>
    </motion.div>
  );
};

export default LoginPage;